// import { Component } from '@theme/component';
// import { debounce, onDocumentLoaded } from '@theme/utilities';
// import { MegaMenuHoverEvent } from '@theme/events';

// const ACTIVATE_DELAY = 0;
// const DEACTIVATE_DELAY = 350;

// /**
//  * A custom element that manages a header menu.
//  *
//  * @typedef {Object} State
//  * @property {HTMLElement | null} activeItem - The currently active menu item.
//  *
//  * @typedef {object} Refs
//  * @property {HTMLElement} overflowMenu - The overflow menu.
//  * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
//  *
//  * @extends {Component<Refs>}
//  */
// class HeaderMenu extends Component {
//   requiredRefs = ['overflowMenu'];

//   #abortController = new AbortController();

//   connectedCallback() {
//     super.connectedCallback();

//     this.overflowMenu?.addEventListener('pointerleave', () => this.#debouncedDeactivate(), {
//       signal: this.#abortController.signal,
//     });

//     onDocumentLoaded(this.#preloadImages);
//   }

//   disconnectedCallback() {
//     super.disconnectedCallback();
//     this.#abortController.abort();
//   }

//   /**
//    * @type {State}
//    */
//   #state = {
//     activeItem: null,
//   };

//   /**
//    * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
//    * @returns {number}
//    */
//   get animationDelay() {
//     const value = this.dataset.animationDelay;
//     return value ? parseInt(value, 10) : 0;
//   }

//   /**
//    * Get the overflow menu
//    */
//   get overflowMenu() {
//     return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
//   }

//   /**
//    * Whether the overflow menu is hovered
//    * @returns {boolean}
//    */
//   get overflowHovered() {
//     return this.refs.overflowMenu?.matches(':hover') ?? false;
//   }

//   /**
//    * Activate the selected menu item immediately
//    * @param {PointerEvent | FocusEvent} event
//    */
//   activate = (event) => {
//     this.#debouncedDeactivate.cancel();
//     this.#debouncedActivateHandler.cancel();

//     this.#debouncedActivateHandler(event);
//   };

//   /**
//    * Activate the selected menu item with a delay
//    * @param {PointerEvent | FocusEvent} event
//    */
//   #activateHandler = (event) => {
//     this.#debouncedDeactivate.cancel();

//     this.dispatchEvent(new MegaMenuHoverEvent());

//     this.removeAttribute('data-animating');

//     if (!(event.target instanceof Element)) return;

//     let item = findMenuItem(event.target);

//     if (!item || item == this.#state.activeItem) return;

//     const isDefaultSlot = event.target.slot === '';

//     this.dataset.overflowExpanded = (!isDefaultSlot).toString();

//     const previouslyActiveItem = this.#state.activeItem;

//     if (previouslyActiveItem) {
//       previouslyActiveItem.ariaExpanded = 'false';
//     }

//     this.#state.activeItem = item;
//     this.ariaExpanded = 'true';
//     item.ariaExpanded = 'true';

//     let submenu = findSubmenu(item);

//     if (!submenu && !isDefaultSlot) {
//       submenu = this.overflowMenu;
//     }

//     const submenuHeight = submenu ? submenu.offsetHeight : 0;

//     this.style.setProperty('--submenu-height', `${submenuHeight}px`);
//     this.style.setProperty('--submenu-opacity', '1');
//   };

//   #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

//   /**
//    * Deactivate the active item after a delay
//    * @param {PointerEvent | FocusEvent} event
//    */
//   deactivate(event) {
//     this.#debouncedActivateHandler.cancel();

//     if (!(event.target instanceof Element)) return;

//     const item = findMenuItem(event.target);

//     // Make sure the item to be deactivated is still the active one. Ideally
//     // we cancelled the debounce before the item was changed, but just in case.
//     if (item === this.#state.activeItem) {
//       this.#debouncedDeactivate();
//     }
//   }

//   /**
//    * Deactivate the active item immediately
//    * @param {HTMLElement | null} [item]
//    */
//   #deactivate = (item = this.#state.activeItem) => {
//     if (!item || item != this.#state.activeItem) return;
//     if (this.overflowHovered) return;

//     this.style.setProperty('--submenu-height', '0px');
//     this.style.setProperty('--submenu-opacity', '0');
//     this.dataset.overflowExpanded = 'false';

//     this.#state.activeItem = null;
//     this.ariaExpanded = 'false';
//     item.ariaExpanded = 'false';
//     item.setAttribute('data-animating', '');

//     setTimeout(() => {
//       item.removeAttribute('data-animating');
//     }, Math.max(0, this.animationDelay - 150)); // Start header transition 150ms before submenu finishes
//   };

//   /**
//    * Deactivate the active item after a delay
//    * @param {PointerEvent | FocusEvent} event
//    */
//   #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

//   /**
//    * Preload images that are set to load lazily.
//    */
//   #preloadImages = () => {
//     const images = this.querySelectorAll('img[loading="lazy"]');
//     images?.forEach((image) => image.removeAttribute('loading'));
//   };
// }

// if (!customElements.get('header-menu')) {
//   customElements.define('header-menu', HeaderMenu);
// }

// /**
//  * Find the closest menu item.
//  * @param {Element | null | undefined} element
//  * @returns {HTMLElement | null}
//  */
// function findMenuItem(element) {
//   if (!(element instanceof Element)) return null;

//   if (element?.matches('[slot="more"')) {
//     // Select the first overflowing menu item when hovering over the "More" item
//     return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
//   }

//   return element?.querySelector('[ref="menuitem"]');
// }

// /**
//  * Find the closest submenu.
//  * @param {Element | null | undefined} element
//  * @returns {HTMLElement | null}
//  */
// function findSubmenu(element) {
//   const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
//   return submenu instanceof HTMLElement ? submenu : null;
// }

import { Component } from '@theme/component';
import { onDocumentLoaded } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * A custom element that manages a header menu.
 * Interaction is click-based (not hover-based).
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    // Close the menu when clicking outside of it
    document.addEventListener('click', this.#handleOutsideClick, {
      signal: this.#abortController.signal,
    });

    // Close the menu when pressing Escape
    document.addEventListener('keydown', this.#handleKeyDown, {
      signal: this.#abortController.signal,
    });

    onDocumentLoaded(this.#preloadImages);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Activate or toggle the selected menu item on click.
   * If the clicked item is already active, it will be deactivated (toggle).
   * Climbs up to the <li> first to reliably find the menuitem ref,
   * regardless of whether the click landed on the <a>, <span>, or <li> itself.
   * @param {MouseEvent | KeyboardEvent} event
   */

  activate = (event) => {
    if (!(event.target instanceof Element)) return;

    console.log('activate called, target:', event.target);

    const li = event.target.closest('.menu-list__list-item');
    console.log('li found:', li);

    if (!li) return;

    const item = findMenuItem(li);
    console.log('item found:', item);

    if (!item) return;

    console.log('activeItem:', this.#state.activeItem);

    // Toggle
    if (item === this.#state.activeItem) {
      this.#deactivate(item);
      return;
    }

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');

    const isDefaultSlot = li.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(li);

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    const submenuHeight = submenu ? submenu.offsetHeight : 0;

    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');
  };

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item !== this.#state.activeItem) return;

    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    setTimeout(() => {
      item.removeAttribute('data-animating');
    }, Math.max(0, this.animationDelay - 150)); // Start header transition 150ms before submenu finishes
  };

  /**
   * Close the active menu when clicking outside the component.
   * @param {MouseEvent} event
   */
  #handleOutsideClick = (event) => {
    if (!(event.target instanceof Element)) return;

    if (!this.contains(event.target)) {
      this.#deactivate();
    }
  };

  /**
   * Close the active menu when pressing Escape.
   * @param {KeyboardEvent} event
   */
  #handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#deactivate();
    }
  };

  /**
   * Prevent navigation for links that have a submenu.
   * The click event bubbles up to the parent <li> which triggers activate().
   * @param {MouseEvent} event
   */
  handleLinkClick = (event) => {
    event.preventDefault();
  };

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the menuitem ref within a given element.
 * Handles the case where the element itself is the menuitem,
 * or where it needs to be found among descendants.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"]')) {
    // Select the first overflowing menu item when clicking the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  // If the element itself is the menuitem ref
  if (element.matches('[ref="menuitem"]')) {
    return /** @type {HTMLElement} */ (element);
  }

  // Otherwise search within descendants
  const found = element.querySelector('[ref="menuitem"]');
  return found instanceof HTMLElement ? found : null;
}

/**
 * Find the submenu within a given <li> element.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}